// src/components/defined/activity_overview/ActivityStructurePanel.jsx

import React, { useMemo, useState } from "react";
import theme from "../../../styles/theme";

/* ============================================================================
   ACTIVITY STRUCTURE PANEL — ZONE A2
   ----------------------------------------------------------------------------
   OBJECTIF
   - compléter A1 sans le dupliquer
   - offrir une lecture synthétique du delivery proposé
   - signaler les tensions structurelles principales
   - permettre quelques réglages globaux simples
   - afficher un contexte léger sur l’élément sélectionné

   PRINCIPES UX
   - sobre, aéré, lisible immédiatement
   - pas de dashboard
   - pas de graphique lourd
   - pas de duplication de la structure A1
============================================================================ */

const T = {
  text: theme?.colors?.text?.primary || "#164D63",
  soft: theme?.colors?.text?.small || "#5C7A89",
  verySoft: theme?.colors?.text?.verysmall || "#7D97A1",
  border: theme?.colors?.borders?.default || "#d6dee4",

  blueText: theme?.Generic?.blue?.pastel?.text || "#1E5066",
  greenText: theme?.Generic?.green?.pastel?.text || "#166A58",
  yellowText: theme?.Generic?.yellow?.pastel?.text || "#8A4A13",
  orangeText: theme?.Generic?.orange?.pastel?.text || "#9A4315",
  greyText: theme?.Generic?.grey?.pastel?.text || "#3F5D6E",
  redText: theme?.Generic?.red?.pastel?.text || "#6E1F1F",
};

const DEFAULT_DELIVERY_METHOD = "EPC";
const DEFAULT_TOTAL_DURATION = 24;

const DEFAULT_PHASES = [
  { code: "1", label: "Pilotage global EPC", percent: 20 },
  { code: "2", label: "Ingénierie", percent: 25 },
  { code: "3", label: "Achats & Approvisionnements", percent: 15 },
  { code: "4", label: "Construction & Montage", percent: 25 },
  { code: "5", label: "Mise en service & Performance", percent: 8 },
  { code: "6", label: "Transfert, Formation & Clôture", percent: 7 },
];

const DEFAULT_ACTIVITIES = [
  { id: "1.1", phaseCode: "1", title: "Phase pré-contractuelle et offre", percent: 45, status: "Validated" },
  { id: "1.2", phaseCode: "1", title: "Cadrage post-award (lancement projet)", percent: 55, status: "Proposed" },

  { id: "2.1", phaseCode: "2", title: "Réaliser les études préliminaires", percent: 15, status: "Validated" },
  { id: "2.2", phaseCode: "2", title: "Réaliser les études de conception", percent: 30, status: "Validated" },
  { id: "2.3", phaseCode: "2", title: "Réaliser les études d’exécution", percent: 28, status: "Proposed" },
  { id: "2.4", phaseCode: "2", title: "Coordonner et valider les études", percent: 17, status: "Adjusted" },
  { id: "2.5", phaseCode: "2", title: "Gérer les modifications techniques", percent: 10, status: "Proposed" },

  { id: "3.1", phaseCode: "3", title: "Préparer la stratégie achats", percent: 14, status: "Validated" },
  { id: "3.2", phaseCode: "3", title: "Lancer les consultations", percent: 16, status: "Validated" },
  { id: "3.3", phaseCode: "3", title: "Analyser et sélectionner les offres", percent: 18, status: "Adjusted" },
  { id: "3.4", phaseCode: "3", title: "Contractualiser et passer commandes", percent: 18, status: "Validated" },
  { id: "3.5", phaseCode: "3", title: "Suivre la fabrication et la qualité", percent: 17, status: "Proposed" },
  { id: "3.6", phaseCode: "3", title: "Gérer le transport et la logistique", percent: 17, status: "Proposed" },

  { id: "4.1", phaseCode: "4", title: "Préparer le chantier", percent: 10, status: "Validated" },
  { id: "4.2", phaseCode: "4", title: "Réaliser les travaux civils", percent: 26, status: "Validated" },
  { id: "4.3", phaseCode: "4", title: "Installer équipements et tuyauteries", percent: 22, status: "Validated" },
  { id: "4.4", phaseCode: "4", title: "Réaliser installations électriques et contrôle-commande", percent: 18, status: "Proposed" },
  { id: "4.5", phaseCode: "4", title: "Réaliser tests de montage", percent: 12, status: "Adjusted" },
  { id: "4.6", phaseCode: "4", title: "Gérer HSE et qualité chantier", percent: 12, status: "Proposed" },

  { id: "5.1", phaseCode: "5", title: "Préparer la mise en service", percent: 22, status: "Validated" },
  { id: "5.2", phaseCode: "5", title: "Réaliser la mise en route", percent: 28, status: "Validated" },
  { id: "5.3", phaseCode: "5", title: "Vérifier les performances contractuelles", percent: 30, status: "Proposed" },
  { id: "5.4", phaseCode: "5", title: "Formaliser la réception provisoire", percent: 20, status: "Proposed" },

  { id: "6.1", phaseCode: "6", title: "Former le personnel client", percent: 22, status: "Proposed" },
  { id: "6.2", phaseCode: "6", title: "Transférer la documentation et les DOE", percent: 28, status: "Validated" },
  { id: "6.3", phaseCode: "6", title: "Assurer la phase de garantie et support", percent: 24, status: "Proposed" },
  { id: "6.4", phaseCode: "6", title: "Clôturer le contrat et réaliser la réception définitive", percent: 26, status: "Proposed" },
];

const formatOneDecimal = (value) => {
  const num = Number(value);
  if (Number.isNaN(num)) return "0.0";
  return num.toFixed(1);
};

const getWeeksFromProjectPercent = (projectPercent, totalDuration) => {
  const totalWeeks = Number(totalDuration) * (52 / 12);
  return Math.round((totalWeeks * Number(projectPercent || 0)) / 100);
};

const getSignalColor = (tone) => {
  switch (tone) {
    case "green":
      return T.greenText;
    case "yellow":
      return T.yellowText;
    case "orange":
      return T.orangeText;
    case "red":
      return T.redText;
    default:
      return T.greyText;
  }
};

const PanelBlock = ({ title, subtitle, children }) => (
  <div
    style={{
      display: "flex",
      flexDirection: "column",
      gap: 10,
      paddingBottom: 2,
    }}
  >
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        gap: subtitle ? 4 : 0,
      }}
    >
      <div
        style={{
          fontFamily: theme?.fonts?.body || "inherit",
          fontSize: "0.84rem",
          lineHeight: 1.12,
          color: theme.colors.text.caneva,
          fontWeight: 600,
        }}
      >
        {title}
      </div>
    </div>

    {children}
  </div>
);

const KeyValueRow = ({ label, value, valueColor = T.text }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr auto",
      alignItems: "center",
      gap: 12,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.74rem",
        lineHeight: 1.2,
        color: T.soft,
        minWidth: 0,
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.76rem",
        lineHeight: 1.2,
        color: valueColor,
        fontWeight: 600,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {value}
    </div>
  </div>
);

const PhaseSummaryRow = ({ label, percent, weeks, emphasis = false }) => (
  <div
    style={{
      display: "grid",
      gridTemplateColumns: "1fr 58px 82px",
      alignItems: "center",
      gap: 10,
      padding: "6px 0",
      borderBottom: `1px solid ${T.border}`,
    }}
  >
    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.74rem",
        lineHeight: 1.2,
        color: emphasis ? T.text : T.soft,
        fontWeight: emphasis ? 600 : 500,
        minWidth: 0,
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
      }}
    >
      {label}
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.74rem",
        lineHeight: 1.1,
        color: T.text,
        fontWeight: 600,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      {formatOneDecimal(percent)}%
    </div>

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.72rem",
        lineHeight: 1.1,
        color: T.verySoft,
        fontWeight: 500,
        textAlign: "right",
        whiteSpace: "nowrap",
      }}
    >
      [{weeks} sem]
    </div>
  </div>
);

const SignalRow = ({ text, tone = "grey" }) => (
  <div
    style={{
      display: "flex",
      alignItems: "flex-start",
      gap: 8,
      padding: "4px 0",
    }}
  >
    <div
      style={{
        width: 5,
        height: 5,
        borderRadius: 999,
        background: getSignalColor(tone),
        marginTop: 6,
        flexShrink: 0,
      }}
    />

    <div
      style={{
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.72rem",
        lineHeight: 1.35,
        color: T.soft,
      }}
    >
      {text}
    </div>
  </div>
);

const CompactSelect = ({ value, onChange, options = [] }) => (
  <div
    style={{
      position: "relative",
      width: "100%",
      height: 28,
      borderRadius: 3,
      border: `1px solid ${T.border}`,
      background: "#ffffff",
      boxSizing: "border-box",
      overflow: "hidden",
    }}
  >
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: "100%",
        height: "100%",
        border: "none",
        background: "transparent",
        color: T.text,
        fontFamily: theme?.fonts?.body || "inherit",
        fontSize: "0.72rem",
        fontWeight: 500,
        padding: "0 24px 0 8px",
        outline: "none",
        appearance: "none",
        WebkitAppearance: "none",
        MozAppearance: "none",
      }}
    >
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>

    <div
      style={{
        position: "absolute",
        right: 8,
        top: "50%",
        transform: "translateY(-50%)",
        pointerEvents: "none",
        fontSize: "0.68rem",
        color: T.soft,
        fontWeight: 700,
      }}
    >
      ▾
    </div>
  </div>
);

const ActivityStructurePanel = ({
  deliveryMethod = DEFAULT_DELIVERY_METHOD,
  totalDuration = DEFAULT_TOTAL_DURATION,
  phases = DEFAULT_PHASES,
  activities = DEFAULT_ACTIVITIES,
  selectedItem = null,
}) => {
  const [overlapIntensity, setOverlapIntensity] = useState("Standard");
  const [distributionLogic, setDistributionLogic] = useState("Balanced");
  const [deliveryRigidity, setDeliveryRigidity] = useState("Standard");

  const totalActivities = activities.length;

  const dominantPhase = useMemo(() => {
    return [...phases].sort((a, b) => (b.percent || 0) - (a.percent || 0))[0] || null;
  }, [phases]);

  const averagePhaseLoad = useMemo(() => {
    if (!phases.length) return 0;
    const sum = phases.reduce((acc, item) => acc + Number(item.percent || 0), 0);
    return sum / phases.length;
  }, [phases]);

  const phaseSummary = useMemo(() => {
    return phases.map((phase) => ({
      ...phase,
      weeks: getWeeksFromProjectPercent(phase.percent, totalDuration),
      activityCount: activities.filter((item) => item.phaseCode === phase.code).length,
    }));
  }, [phases, activities, totalDuration]);

  const tensionSignals = useMemo(() => {
    const signals = [];

    const sorted = [...phaseSummary].sort((a, b) => b.percent - a.percent);
    const strongest = sorted[0];
    const weakest = sorted[sorted.length - 1];

    if (strongest && strongest.percent >= 25) {
      signals.push({
        tone: "orange",
        text: `Charge concentrée sur ${strongest.label}.`,
      });
    }

    if (weakest && weakest.percent <= 8) {
      signals.push({
        tone: "yellow",
        text: `${weakest.label} apparaît relativement courte au regard du cycle projet.`,
      });
    }

    const engineering = phaseSummary.find((item) => item.code === "2");
    const procurement = phaseSummary.find((item) => item.code === "3");
    const commissioning = phaseSummary.find((item) => item.code === "5");

    if (engineering && procurement && engineering.percent >= 20 && procurement.percent >= 15) {
      signals.push({
        tone: "grey",
        text: "Chevauchement probable entre Ingénierie et Achats à surveiller.",
      });
    }

    if (commissioning && commissioning.percent <= 8) {
      signals.push({
        tone: "yellow",
        text: "La mise en service semble tendue en fin de séquence.",
      });
    }

    if (!signals.length) {
      signals.push({
        tone: "green",
        text: "La distribution globale apparaît équilibrée à ce stade.",
      });
    }

    return signals.slice(0, 4);
  }, [phaseSummary]);

  const selectedContext = useMemo(() => {
    if (!selectedItem) return null;

    if (selectedItem.level === "group") {
      const phase = phaseSummary.find((item) => item.code === selectedItem.code);
      return {
        kind: "phase",
        title: selectedItem.title,
        rows: [
          { label: "Project load", value: `${formatOneDecimal(phase?.percent ?? 0)}%` },
          { label: "Estimated duration", value: `${phase?.weeks ?? 0} weeks` },
          { label: "Activities", value: `${phase?.activityCount ?? 0}` },
          { label: "Role", value: "Major delivery segment" },
        ],
      };
    }

    if (selectedItem.level === "activity") {
      const parentPhase = phaseSummary.find(
        (item) => item.code === String(selectedItem.code).split(".")[0]
      );

      const activityWeeks = Math.round(
        ((parentPhase?.weeks || 0) * Number(selectedItem.phasePercent || 0)) / 100
      );

      return {
        kind: "activity",
        title: selectedItem.title,
        rows: [
          { label: "Parent phase", value: parentPhase?.label || "-" },
          { label: "Phase load", value: `${formatOneDecimal(selectedItem.phasePercent || 0)}%` },
          { label: "Estimated duration", value: `${activityWeeks} weeks` },
          { label: "Status", value: selectedItem.status || "Proposed" },
        ],
      };
    }

    return null;
  }, [selectedItem, phaseSummary]);

  return (
    <div
      style={{
        width: "100%",
        display: "flex",
        flexDirection: "column",
        gap: 22,
        boxSizing: "border-box",
      }}
    >
      {/* EN-TÊTE */}
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          minHeight: 12,
        }}
      >
        <div
          style={{
            fontFamily: theme?.fonts?.body || "inherit",
            fontSize: "1.02rem",
            lineHeight: 1.1,
            color: T.text,
            fontWeight: 600,
          }}
        >
          Planning Overview
        </div>
      </div>

      {/* 1 — PLANNING SNAPSHOT */}
      <PanelBlock title="Planning snapshot">
        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
          <KeyValueRow label="Delivery method" value={deliveryMethod} />
          <KeyValueRow label="Total duration" value={`${totalDuration} months`} />
          <KeyValueRow label="Phases" value={`${phases.length}`} />
          <KeyValueRow label="Activities" value={`${totalActivities}`} />
          <KeyValueRow
            label="Dominant phase"
            value={dominantPhase?.label || "-"}
            valueColor={T.blueText}
          />
          <KeyValueRow
            label="Average phase load"
            value={`${formatOneDecimal(averagePhaseLoad)}%`}
          />
        </div>
      </PanelBlock>

      {/* 2 — PHASE LOAD SUMMARY */}
      <PanelBlock title="Phase load summary">
        <div style={{ display: "flex", flexDirection: "column" }}>
          {phaseSummary.map((phase, index) => (
            <PhaseSummaryRow
              key={phase.code}
              label={phase.label}
              percent={phase.percent}
              weeks={phase.weeks}
              emphasis={index === 0 || phase.code === dominantPhase?.code}
            />
          ))}
        </div>
      </PanelBlock>

      {/* 3 — TENSION SIGNALS */}
      <PanelBlock title="Tension signals">
        <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
          {tensionSignals.map((signal, index) => (
            <SignalRow key={`${signal.text}-${index}`} text={signal.text} tone={signal.tone} />
          ))}
        </div>
      </PanelBlock>

      {/* 4 — GLOBAL ADJUSTMENTS */}
      <PanelBlock title="Global adjustments">
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div
              style={{
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.72rem",
                lineHeight: 1.1,
                color: T.soft,
                fontWeight: 500,
              }}
            >
              Intensité de chevauchement
            </div>
            <CompactSelect
              value={overlapIntensity}
              onChange={setOverlapIntensity}
              options={["Faible", "Standard", "Élevée"]}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div
              style={{
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.72rem",
                lineHeight: 1.1,
                color: T.soft,
                fontWeight: 500,
              }}
            >
              Logique de répartition
            </div>
            <CompactSelect
              value={distributionLogic}
              onChange={setDistributionLogic}
              options={["Équilibrée", "Chargée en amont", "Chargée en aval"]}
            />
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
            <div
              style={{
                fontFamily: theme?.fonts?.body || "inherit",
                fontSize: "0.72rem",
                lineHeight: 1.1,
                color: T.soft,
                fontWeight: 500,
              }}
            >
              Rigidité du delivery
            </div>
            <CompactSelect
              value={deliveryRigidity}
              onChange={setDeliveryRigidity}
              options={["Flexible", "Standard", "Stricte"]}
            />
          </div>
        </div>
      </PanelBlock>

      {/* 5 — SELECTED CONTEXT */}
      {selectedContext && (
        <PanelBlock title="Selected context">
          <div
            style={{
              fontFamily: theme?.fonts?.body || "inherit",
              fontSize: "0.78rem",
              lineHeight: 1.2,
              color: T.text,
              fontWeight: 600,
              marginBottom: 2,
            }}
          >
            {selectedContext.title}
          </div>

          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {selectedContext.rows.map((row) => (
              <KeyValueRow
                key={`${selectedContext.title}-${row.label}`}
                label={row.label}
                value={row.value}
              />
            ))}
          </div>
        </PanelBlock>
      )}
    </div>
  );
};

export default ActivityStructurePanel;